<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import About from './components/About.svelte';
  import AddPlotRow from './components/AddPlotRow.svelte';
  import AnalysisPanel from './components/AnalysisPanel.svelte';
  import FocusControls from './components/FocusControls.svelte';
  import NavBar from './components/NavBar.svelte';
  import PlotPanel from './components/PlotPanel.svelte';
  import WorkspaceControls from './components/WorkspaceControls.svelte';
  import { preloadChartRuntime } from './lib/highcharts-runtime';
  import { createReloadScrollRestorer, type ReloadScrollRestorer } from './lib/reload-scroll';
  import { requestManager } from './lib/request-manager';
  import { normalizePlotSpec, plotByKind } from './lib/registry';
  import { panelSlide } from './lib/panel-transition';
  import { applyScanCursor, firstScanLeader, isScannable, reconcileScanLeader, scanTargets } from './lib/scan';
  import { clampTimestamp } from './lib/time';
  import { applyTheme, readTheme, type ThemePreference } from './lib/theme';
  import type { AnalysisState, ComparisonMode, Monitor, PanelNavigation, PlotAnalysisSource, PlotSpec, WorkspaceState } from './lib/types';
  import { combinedSearchParams, createDefaultAnalysisState, createDefaultWorkspace, createPlotId, DEFAULT_COMPARISON_MODE, MAX_WORKSPACE_PLOTS, parseAnalysisState, parseComparisonMode, parseWorkspace, workspaceUrl } from './lib/workspace-state';

  let activePage: 'home' | 'about' = 'home';
  let theme: ThemePreference = 'system';
  let monitors: Monitor[] = [];
  let workspace: WorkspaceState | null = null;
  let comparisonMode: ComparisonMode = DEFAULT_COMPARISON_MODE;
  let initializing = true;
  let error = '';
  let cursorPreview = 0;
  let scanLeaderId = '';
  let activePanelId = '';
  let navigationByPanel: Record<string, PanelNavigation> = {};
  let analysisState: AnalysisState = createDefaultAnalysisState();
  let analysisSources: Record<string, PlotAnalysisSource> = {};
  let waveformAnalysisSources: Record<string, PlotAnalysisSource> = {};
  let persistTimer: number | null = null;
  let cursorCommitTimer: number | null = null;
  let requestsActive = false;
  let reloadScrollRestorer: ReloadScrollRestorer | null = null;
  let unsubscribeActivity: (() => void) | null = null;
  let mediaQuery: MediaQueryList | null = null;
  const systemChanged = () => { if (theme === 'system') applyTheme('system'); };

  $: scanNavigation = workspace ? scanTargets(workspace, scanLeaderId, navigationByPanel) : { previous: null, next: null };
  $: previousTimestamp = scanNavigation.previous;
  $: nextTimestamp = scanNavigation.next;
  $: hasFocusControls = workspace ? workspace.plots.some(isScannable) : false;
  $: globallyLoading = initializing || requestsActive;
  $: reloadScrollRestorer?.setEnabled(activePage === 'home');
  $: reloadScrollRestorer?.setRequestsActive(globallyLoading);

  onMount(async () => {
    reloadScrollRestorer = createReloadScrollRestorer();
    unsubscribeActivity = requestManager.subscribeActivity((pending) => {
      const active = pending > 0;
      if (requestsActive !== active) requestsActive = active;
    });
    theme = readTheme();
    applyTheme(theme);
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', systemChanged);
    try {
      const allMonitors = await requestManager.json<Monitor[]>('/monitors', { retry503: true });
      const validMonitors = allMonitors.filter((monitor) => monitor.total_rows > 0 && monitor.earliest_date < monitor.latest_date);
      if (validMonitors.length === 0) throw new Error('No monitoring locations with RMS data were returned.');
      monitors = validMonitors;
      const defaultMonitor = validMonitors[0];
      const params = combinedSearchParams(window.location);
      comparisonMode = parseComparisonMode(params);
      let remembered: string | null = null;
      try { remembered = localStorage.getItem('pq-monitor'); } catch { /* storage can be disabled */ }
      const preferredMonitorId = params.get('location') ?? remembered;
      const rangeMonitor = validMonitors.find((monitor) => monitor.monitor_name_index === preferredMonitorId) ?? defaultMonitor;
      workspace = parseWorkspace(params, {
        defaultMonitorId: defaultMonitor.monitor_name_index,
        defaultRange: [rangeMonitor.earliest_date, rangeMonitor.latest_date],
        rememberedMonitorId: remembered,
        validMonitorIds: new Set(validMonitors.map((monitor) => monitor.monitor_name_index))
      });
      preloadChartRuntime(workspace.plots.map((plot) => plot.kind));
      analysisState = parseAnalysisState(params, workspace);
      if (!params.has('w') && !params.has('workspace') && !params.has('cursor') && workspace.plots.length === 1) {
        const domain = plotByKind(workspace.plots[0].kind).timeDomain;
        const selectedMonitor = validMonitors.find((monitor) => monitor.monitor_name_index === workspace?.globalMonitorId) ?? rangeMonitor;
        if (domain === 'snapshot' || domain === 'relative') workspace.cursor = clampTimestamp(selectedMonitor.harmonics_earliest_date, workspace.range);
        if (domain === 'day') workspace.cursor = clampTimestamp(selectedMonitor.earliest_date + 86_400, workspace.range);
      }
      cursorPreview = workspace.cursor;
      scanLeaderId = firstScanLeader(workspace.plots);
      activePanelId = workspace.plots.find((plot) => plot.selectedEvent)?.id ?? workspace.plots[0]?.id ?? '';
    } catch (reason) {
      error = reason instanceof Error ? reason.message : 'Unable to initialize the workspace.';
    } finally {
      initializing = false;
    }
  });

  function persist() {
    if (!workspace) return;
    if (persistTimer != null) window.clearTimeout(persistTimer);
    persistTimer = window.setTimeout(() => {
      if (!workspace) return;
      try {
        const next = new URL(workspaceUrl(workspace, comparisonMode, new URL(window.location.href), analysisState), window.location.origin);
        history.replaceState(null, '', `${next.pathname}${next.search}${next.hash}`);
      } catch (reason) {
        console.warn('The workspace URL was not updated.', reason);
      }
    }, 80);
  }

  function replaceWorkspace(next: WorkspaceState) {
    workspace = next;
    persist();
  }

  function replaceAnalysisState(next: AnalysisState) {
    analysisState = next;
    persist();
  }

  function setGlobalMonitor(monitorId: string) {
    if (!workspace) return;
    replaceWorkspace({ ...workspace, globalMonitorId: monitorId });
    try { localStorage.setItem('pq-monitor', monitorId); } catch { /* storage can be disabled */ }
  }

  function setRange(range: [number, number]) {
    if (!workspace || range[0] >= range[1]) return;
    cancelCursorCommit();
    const cursor = clampTimestamp(workspace.cursor, range);
    cursorPreview = cursor;
    replaceWorkspace({ ...workspace, range, cursor });
  }

  function resetRange() {
    if (!workspace) return;
    const monitor = monitors.find((item) => item.monitor_name_index === workspace?.globalMonitorId);
    if (monitor) setRange([monitor.earliest_date, monitor.latest_date]);
  }

  function previewCursor(cursor: number) {
    if (!workspace) return;
    const next = clampTimestamp(cursor, workspace.range);
    cursorPreview = next;
    cancelCursorCommit();
    cursorCommitTimer = window.setTimeout(() => {
      cursorCommitTimer = null;
      commitCursor(next);
    }, 120);
  }

  function commitCursor(cursor: number) {
    if (!workspace) return;
    cancelCursorCommit();
    const next = clampTimestamp(cursor, workspace.range);
    cursorPreview = next;
    if (workspace.cursor === next) return;
    replaceWorkspace({ ...workspace, cursor: next });
  }

  function cancelCursorCommit() {
    if (cursorCommitTimer != null) window.clearTimeout(cursorCommitTimer);
    cursorCommitTimer = null;
  }

  function step(direction: -1 | 1) {
    const target = direction < 0 ? previousTimestamp : nextTimestamp;
    if (target == null || !workspace) return;
    cancelCursorCommit();
    const next = applyScanCursor(workspace, scanLeaderId, target);
    cursorPreview = next.cursor;
    replaceWorkspace(next);
  }

  function selectPanelEvent(plot: PlotSpec, monitorId: string, cursor: number) {
    if (!workspace) return;
    cancelCursorCommit();
    const next = applyScanCursor(workspace, plot.id, cursor);
    next.plots = next.plots.map((item) => item.id === plot.id
      ? normalizePlotSpec({ ...item, selectedEvent: { monitorId, timestamp: cursor } })
      : item);
    cursorPreview = next.cursor;
    replaceWorkspace(next);
  }

  function addPlot(plot: PlotSpec) {
    if (!workspace || workspace.plots.length >= MAX_WORKSPACE_PLOTS) return;
    replaceWorkspace({ ...workspace, plots: [...workspace.plots, plot] });
    if (!activePanelId) activePanelId = plot.id;
    if (!scanLeaderId && isScannable(plot)) scanLeaderId = plot.id;
  }

  function updatePlot(id: string, nextPlot: PlotSpec) {
    if (!workspace) return;
    const plots = workspace.plots.map((plot) => plot.id === id ? nextPlot : plot);
    scanLeaderId = reconcileScanLeader(plots, scanLeaderId);
    replaceWorkspace({ ...workspace, plots });
  }

  function duplicatePlot(index: number) {
    if (!workspace || workspace.plots.length >= MAX_WORKSPACE_PLOTS) return;
    const source = workspace.plots[index];
    const duplicate: PlotSpec = {
      ...source,
      id: createPlotId(),
      options: { ...source.options },
      location: { ...source.location },
      hiddenEventClassifications: source.hiddenEventClassifications
        ? [...source.hiddenEventClassifications]
        : undefined
    };
    const plots = [...workspace.plots];
    plots.splice(index + 1, 0, duplicate);
    replaceWorkspace({ ...workspace, plots });
  }

  function movePlot(index: number, direction: -1 | 1) {
    if (!workspace) return;
    const target = index + direction;
    if (target < 0 || target >= workspace.plots.length) return;
    const plots = [...workspace.plots];
    [plots[index], plots[target]] = [plots[target], plots[index]];
    replaceWorkspace({ ...workspace, plots });
  }

  function removePlot(index: number) {
    if (!workspace) return;
    const removed = workspace.plots[index];
    const plots = workspace.plots.filter((_, itemIndex) => itemIndex !== index);
    const nextNavigation = { ...navigationByPanel };
    delete nextNavigation[removed.id];
    navigationByPanel = nextNavigation;
    const nextSeriesByPlot = { ...analysisState.seriesByPlot };
    delete nextSeriesByPlot[removed.id];
    const nextWaveformSeriesByPlot = { ...analysisState.waveformSeriesByPlot };
    delete nextWaveformSeriesByPlot[removed.id];
    analysisState = {
      ...analysisState,
      selectedPlotIds: analysisState.selectedPlotIds.filter((id) => id !== removed.id),
      seriesByPlot: nextSeriesByPlot,
      waveformSeriesByPlot: nextWaveformSeriesByPlot
    };
    const nextAnalysisSources = { ...analysisSources };
    delete nextAnalysisSources[removed.id];
    analysisSources = nextAnalysisSources;
    const nextWaveformAnalysisSources = { ...waveformAnalysisSources };
    delete nextWaveformAnalysisSources[removed.id];
    waveformAnalysisSources = nextWaveformAnalysisSources;
    if (activePanelId === removed.id) activePanelId = plots.find((plot) => plot.selectedEvent)?.id ?? plots[0]?.id ?? '';
    if (scanLeaderId === removed.id) scanLeaderId = firstScanLeader(plots);
    replaceWorkspace({ ...workspace, plots });
  }

  function focusPlot(plot: PlotSpec) {
    activePanelId = plot.id;
    if (isScannable(plot)) scanLeaderId = plot.id;
  }

  function navigationChanged(navigation: PanelNavigation) {
    navigationByPanel = { ...navigationByPanel, [navigation.panelId]: navigation };
  }

  function analysisDataChanged(plotId: string, source: PlotAnalysisSource | null) {
    if (source) {
      if (analysisSources[plotId] === source) return;
      analysisSources = { ...analysisSources, [plotId]: source };
      return;
    }
    if (!(plotId in analysisSources)) return;
    const next = { ...analysisSources };
    delete next[plotId];
    analysisSources = next;
  }

  function waveformAnalysisDataChanged(plotId: string, source: PlotAnalysisSource | null) {
    if (source) {
      if (waveformAnalysisSources[plotId] === source) return;
      waveformAnalysisSources = { ...waveformAnalysisSources, [plotId]: source };
      return;
    }
    if (!(plotId in waveformAnalysisSources)) return;
    const next = { ...waveformAnalysisSources };
    delete next[plotId];
    waveformAnalysisSources = next;
  }

  function setComparisonMode(next: ComparisonMode) {
    comparisonMode = next;
    persist();
  }

  function setTheme(next: ThemePreference) {
    theme = next;
    applyTheme(next);
  }

  onDestroy(() => {
    if (persistTimer != null) window.clearTimeout(persistTimer);
    cancelCursorCommit();
    unsubscribeActivity?.();
    reloadScrollRestorer?.destroy();
    mediaQuery?.removeEventListener('change', systemChanged);
  });
</script>

<NavBar active={activePage} {theme} busy={globallyLoading} onNavigate={(page) => activePage = page} onTheme={setTheme} />

{#if activePage === 'about'}
  <main class="page"><About /></main>
{:else}
  <main class="page page--workspace">
    {#if initializing}
      <div class="initial-state" role="status">Loading monitoring locations…</div>
    {:else if error}
      <div class="initial-state initial-state--error" role="alert">
        <h1>Workspace unavailable</h1><p>{error}</p>
      </div>
    {:else if workspace}
      <div class="workspace-toolbar">
        <WorkspaceControls
          {workspace}
          {monitors}
          {comparisonMode}
          onGlobalMonitor={setGlobalMonitor}
          onRange={setRange}
          onResetRange={resetRange}
          onComparisonMode={setComparisonMode}
          analysisOpen={analysisState.open}
          onToggleAnalysis={() => replaceAnalysisState({ ...analysisState, open: !analysisState.open })}
        />
        {#if hasFocusControls}
          <FocusControls
            {workspace}
            {cursorPreview}
            {previousTimestamp}
            {nextTimestamp}
            onCursorPreview={previewCursor}
            onCursorCommit={commitCursor}
            onStep={step}
          />
        {/if}
        <AddPlotRow onAdd={addPlot} disabled={workspace.plots.length >= MAX_WORKSPACE_PLOTS} />
        {#if analysisState.open}
          <div class="panel-transition panel-transition--analysis" transition:panelSlide>
            <AnalysisPanel
              plots={workspace.plots}
              sources={analysisSources}
              waveformSources={waveformAnalysisSources}
              state={analysisState}
              onStateChange={replaceAnalysisState}
              {cursorPreview}
              onCursorPreview={previewCursor}
            />
          </div>
        {/if}
      </div>
      <section class="plot-stack" aria-label="Workspace plots">
        {#if workspace.plots.length === 0}
          <div class="panel-transition panel-transition--empty" transition:panelSlide>
            <div class="empty-workspace"><h2>No plots yet</h2><p>Choose a parameter and plot type above to begin.</p></div>
          </div>
        {/if}
        {#each workspace.plots as plot, index (plot.id)}
          <div class="panel-transition panel-transition--plot" transition:panelSlide>
            <PlotPanel
              {plot}
              {workspace}
              {monitors}
              {comparisonMode}
              {cursorPreview}
              {index}
              count={workspace.plots.length}
              onUpdate={(next) => updatePlot(plot.id, next)}
              onDuplicate={() => duplicatePlot(index)}
              duplicateDisabled={workspace.plots.length >= MAX_WORKSPACE_PLOTS}
              onMove={(direction) => movePlot(index, direction)}
              onRemove={() => removePlot(index)}
              onFocused={() => focusPlot(plot)}
              keyboardActive={activePanelId === plot.id}
              onNavigation={navigationChanged}
              onRangeChange={setRange}
              onResetRange={resetRange}
              onEventSelect={(monitorId, cursor) => selectPanelEvent(plot, monitorId, cursor)}
              onCursorPreview={previewCursor}
              analysisRequested={analysisState.dataMode === 'plot-data' && analysisState.selectedPlotIds.includes(plot.id)}
              onAnalysisData={analysisDataChanged}
              waveformAnalysisRequested={analysisState.dataMode === 'event-waveforms' && analysisState.selectedPlotIds.includes(plot.id)}
              onWaveformAnalysisData={waveformAnalysisDataChanged}
            />
          </div>
        {/each}
      </section>
    {/if}
  </main>
{/if}
